import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import { captureException } from './lib/monitoring';

// 🔧 Lazy imports
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const ProviderRegister = lazy(() => import('./pages/ProviderRegister/index'));
const SignIn = lazy(() => import('./pages/SignIn'));
const AwaitingApproval = lazy(() => import('./pages/AwaitingApproval')); // ✅ Added new page
// Support & Legal pages (simple placeholders)
const Help = lazy(() => import('./pages/Support/Help'));
const ContactSupport = lazy(() => import('./pages/Support/ContactSupport'));
const ServiceAreas = lazy(() => import('./pages/Support/ServiceAreas'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Cookies = lazy(() => import('./pages/Legal/Cookies'));
// Profile pages
const Profile = lazy(() => import('./pages/Profile'));
const ProfileSettings = lazy(() => import('./pages/Profile/Settings'));
// Account settings (private/system-level)
const AccountSettings = lazy(() => import('./pages/Account'));
const SignUp = lazy(() => import('./pages/SignUp/index'));

// Progressive loading indicator
const PageLoader: React.FC<{ delay?: number }> = ({ delay = 300 }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  if (!show) return null;

  return (
    <div className="flex flex-col justify-center items-center min-h-64 space-y-4">
      <div className="relative">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
      <div className="text-center space-y-2">
        <p className="text-gray-600 font-medium">Loading page content...</p>
        <div className="flex justify-center space-x-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Error boundary for lazy-loaded pages
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Keep console logs for local development diagnostics
    // eslint-disable-next-line no-console
    console.error('App Error:', error, errorInfo);
    // Report to Sentry in production with extra context
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-96 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">😵</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">We're having trouble loading this page.</p>
            <button
              onClick={() => globalThis.location?.reload?.()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Scroll to top when route changes
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    globalThis.scrollTo?.(0, 0);
  }, [pathname]);
  return null;
};

// 404 fallback page
const NotFound: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-md mx-auto">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-2xl font-bold text-white">404</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Page Not Found</h1>
          <p className="text-gray-600 mb-8">
            The page you're looking for doesn't exist or has been moved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-xl hover:from-blue-500 hover:to-cyan-500 transition-all duration-200"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Smooth route loading transitions
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 150);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div
      className="grid min-h-screen grid-rows-[auto_1fr_auto] relative"
    >
      {/* Global decorative background layer */}
      <div className="site-bg" aria-hidden="true" />
      <ScrollToTop />

      {/* Header */}
      <Navbar />
      {isLoading && (
        <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500 w-full overflow-hidden">
          <div className="h-full bg-white animate-pulse w-1/2 opacity-30"></div>
        </div>
      )}

      {/* Body */}
      <main className="min-h-0">
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/provider/register" element={<ProviderRegister />} />

              {/* ✅ Awaiting Approval Page */}
              <Route path="/awaiting-approval" element={<AwaitingApproval />} />

              {/* 👤 User Profile (protected) */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/settings"
                element={
                  <ProtectedRoute>
                    <ProfileSettings />
                  </ProtectedRoute>
                }
              />

              {/* 🔐 Account Settings (protected) */}
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route path="/account/settings" element={<Navigate to="/account" replace />} />
              <Route path="/settings" element={<Navigate to="/account" replace />} />

              {/* 404 fallback */}
              {/* Support */}
              <Route path="/help" element={<Help />} />
              <Route path="/support" element={<ContactSupport />} />
              <Route path="/areas" element={<ServiceAreas />} />

              {/* Legal */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />

              {/* 404 fallback */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />
    </div>
  );
};

export default App;
